import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import useMessageStream from '../hooks/useMessageStream';
import { useAuth } from '../contexts/AuthContext';

/**
 * Tiny live-collab toaster.
 *
 * Mounted once at the top of CrmLayout. Subscribes to `crm_changed` SSE
 * events and surfaces a discrete toast in the corner whenever a teammate
 * makes a change. Suppresses your own actions (the backend already excludes
 * the actor from recipients, but we double-check here in case both your
 * tabs share a cookie).
 *
 * Renders nothing.
 */
const SECTION_LABEL = {
  bookings: 'booking',
  customers: 'customer',
  employees: 'employee',
  shifts: 'shift',
  paystubs: 'paystub',
  employee_messages: 'message',
  job_applications: 'job application',
  contact_inquiries: 'contact inquiry',
};

const ACTION_VERB = {
  deleted: 'deleted',
  cancelled: 'cancelled',
  created: 'created',
  updated: 'updated',
  reassigned: 'reassigned',
  rescheduled: 'rescheduled',
};

const dedupeWindowMs = 2000;

export const LiveActivityToaster = () => {
  const { user } = useAuth();
  const recentKeys = useRef(new Map());   // key → lastShownAt

  // Garbage-collect dedupe map every 10s
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      for (const [k, t] of recentKeys.current.entries()) {
        if (now - t > 10_000) recentKeys.current.delete(k);
      }
    }, 10_000);
    return () => clearInterval(id);
  }, []);

  useMessageStream((event) => {
    if (!event || event.type !== 'crm_changed') return;
    // Self-suppression (also handled server-side, but keep the client guard
    // in case both your tabs share the cookie).
    if (event.actor_id && user?.user_id && event.actor_id === user.user_id) return;
    // Dedupe identical broadcasts within a short window.
    const key = `${event.section}|${event.action}|${(event.ids || []).join(',')}`;
    const now = Date.now();
    const last = recentKeys.current.get(key);
    if (last && now - last < dedupeWindowMs) return;
    recentKeys.current.set(key, now);

    const who = event.actor_name || 'A teammate';
    const verb = ACTION_VERB[event.action] || event.action;
    const noun = SECTION_LABEL[event.section] || event.section;
    const label = event.label;
    const count = (event.ids || []).length;
    let line;
    if (label) {
      line = `${who} just ${verb} ${label}`;
    } else if (count > 1) {
      line = `${who} just ${verb} ${count} ${noun}s`;
    } else {
      line = `${who} just ${verb} a ${noun}`;
    }
    toast(line, {
      description: 'Your view will refresh automatically.',
      duration: 4500,
      position: 'bottom-right',
      // sonner exposes a `data-testid` opt-out; we use a class so e2e can find it
      className: 'live-collab-toast',
    });
  });

  return null;
};

export default LiveActivityToaster;
